import { useRouteMatch, Prompt } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import React, { useState, useEffect } from "react";
import { SurgeLogix } from "../../dtos/surgeLogix";
import { Pump } from "../../dtos/pump";
import { useMessage } from "../messageService";
import MaterialTable, { HeaderCells } from "../shared/materialTable";
import EditSurgeLogix from "./editSurgeLogixModal";
import { useApi } from "../apiService";
import { AjaxResult } from "../../enums/ajaxResult";
import { useConfirmDialog } from "../confirmDialogService";
import * as mui from "@material-ui/core";
import PlayCircleOutlineIcon from "@material-ui/icons/PlayCircleOutline";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import RefreshIcon from "@material-ui/icons/Refresh";
import { useLeavingPageAlert } from "../../hooks/useLeavingPageAlert";
import { PumpParameter } from "../../dtos/pumpCommandParameter";

type SurgeLogixValueName = "Count" | "Duration" | "Adjust";

const surgeLogixStartParameterNumber = 300;

const getSurgeLogixParameterNumber = (
  surgeLogix: SurgeLogix,
  valueName: SurgeLogixValueName
) => {
  const typeOffset = surgeLogix.type.toLowerCase() === "advance" ? 0 : 18;
  const valueOffset =
    valueName === "Count" ? 0 : valueName === "Duration" ? 6 : 12;

  return (
    surgeLogixStartParameterNumber +
    (surgeLogix.pumpChannel - 1) +
    typeOffset +
    valueOffset
  );
};

const getSurgeLogixValue = (
  surgeLogix: SurgeLogix,
  valueName: SurgeLogixValueName
) => {
  if (valueName === "Count") {
    return surgeLogix.count;
  }
  if (valueName === "Duration") {
    return surgeLogix.duration;
  }
  return surgeLogix.adjust;
};

const SurgeLogixPage = () => {
  const match = useRouteMatch<{ pumpId: string | undefined }>();
  const pumpId = match.params.pumpId ? parseInt(match.params.pumpId) : 0;
  const { pumpsApi, surgeLogixApi } = useApi();
  const { showMessage } = useMessage();
  const [pump, setPump] = useState<Pump | null>(null);
  const classes = useStyles();
  const { showConfirmDialog } = useConfirmDialog();

  const [
    pumpInconsistentWithCommands,
    setPumpInconsistentWithCommands
  ] = useState(false);
  const [
    pumpParameterRefreshKey,
    setPumpParameterRefreshKey
  ] = useState(0);

  useLeavingPageAlert(pumpInconsistentWithCommands);

  useEffect(() => {
    const fetchPumpData = async () => {
      showMessage("Fetching pumps...");
      pumpsApi.getById(pumpId).then(r => {
        if (r.result === AjaxResult.Success && r.data) {
          showMessage("Pumps fetched!");
          setPump(r.data);
        }
      });
    };
    fetchPumpData();
  }, [showMessage, pumpsApi, pumpId, setPump]);

  const trySurgeLogixCommand = () => {
    showConfirmDialog(
      `Are you sure you want to send surgeLogix to ${pump?.name ? " " + pump.name : ""
      }?`
    ).then(confirmed => {
      if (confirmed) {
        surgeLogixApi.sendToPump(pumpId).then(result => {
          if (result.result === AjaxResult.Success) {
            showMessage("Command sent to Pump");
            setPumpInconsistentWithCommands(false);
            setPumpParameterRefreshKey(key => key + 1);
          } else if (result.messages) {
            result.messages.forEach(m => {
              showMessage(m);
            });
          }
        });
      }
    });
  };

  return (
    <>
      <Prompt
        when={pumpInconsistentWithCommands}
        message="You have changes that have not been pushed to the pump, are you sure you want to leave?"
      />
      <div className={classes.root}>
        <div className={classes.sectionHeader}>
          <div className={classes.headerSection}>
            <label>{pump ? pump.name : "Pump "}</label>
            <div className={classes.serialNumber}>
              {pump ? pump.serialNumber : "SN"}
            </div>
          </div>
          <div className={classes.headerSection}>
            <mui.Button
              variant="contained"
              color="primary"
              className={classes.AutoControlButton}
              endIcon={<PlayCircleOutlineIcon />}
              onClick={trySurgeLogixCommand}
            >
              Send to Pump
            </mui.Button>
          </div>
        </div>
        <SurgeLogixTable
          markStepsChanged={() => setPumpInconsistentWithCommands(true)}
          pumpParameterRefreshKey={pumpParameterRefreshKey}
        />
      </div>
    </>
  );
};

const SurgeLogixTable = (props: {
  markStepsChanged: () => void;
  pumpParameterRefreshKey: number;
}) => {
  const [surgeLogixs, setSurgeLogixs] = useState<SurgeLogix[]>([]);
  const [pumpParameters, setPumpParameters] = useState<PumpParameter[]>([]);
  const [pumpParametersLoaded, setPumpParametersLoaded] = useState(false);
  const [pumpParameterInfoAnchor, setPumpParameterInfoAnchor] = useState<HTMLElement | null>(null);
  const [showAddNew, setShowAddNew] = useState(false);
  const { showConfirmDialog } = useConfirmDialog();
  const { pumpsApi, surgeLogixApi } = useApi();
  const { showMessage } = useMessage();
  const classes = useStyles();
  const [
    selectedSurgeLogix,
    setSelectedSurgeLogix
  ] = useState<SurgeLogix | null>(null);

  const match = useRouteMatch<{ pumpId: string | undefined }>();
  const pumpId = match.params.pumpId ? parseInt(match.params.pumpId) : 0;

  const addUpdateSurgeLogix = (surgeLogix: SurgeLogix | null) => {
    setSelectedSurgeLogix(surgeLogix);
    setShowAddNew(true);
  };
  const zeroSurgeLogix = (surgeLogix: SurgeLogix) => {
    showConfirmDialog(
      `Are you sure you want to clear ${surgeLogix.fieldName ? " " + surgeLogix.fieldName : ""
      }?`
    ).then(confirmed => {
      if (confirmed) {
        surgeLogix.count = 0;
        surgeLogix.duration = 0;
        surgeLogix.adjust = 0;
        surgeLogixApi.putOne(surgeLogix, surgeLogix.id).then(result => {
          if (result.result === AjaxResult.Success) {
            showMessage("Field deleted");
            props.markStepsChanged();
            finishOperation();
          } else if (result.messages) {
            result.messages.forEach(m => {
              showMessage(m);
            });
          }
        });
      }
    });
  };
  const finishOperation = () => {
    setShowAddNew(false);
    setSelectedSurgeLogix(null);
    fetchData();
    fetchPumpParameters();
  };

  const fetchData = async () => {
    showMessage("Fetching Entries...");
    surgeLogixApi.getAllbyPump(pumpId).then(r => {
      if (r.result === AjaxResult.Success && r.data) {
        showMessage("Entries fetched!");
        setSurgeLogixs(r.data);
      }
    });
  };

  const fetchPumpParameters = async () => {
    setPumpParametersLoaded(false);
    pumpsApi.getParameters(pumpId).then(r => {
      if (r.result === AjaxResult.Success && r.data) {
        setPumpParameters(r.data.parms || []);
      } else {
        setPumpParameters([]);
      }
      setPumpParametersLoaded(true);
    });
  };

  const getPumpParameterValue = (parameterNumber: number) => {
    const parameter = pumpParameters.find(p => p.number === parameterNumber);
    return parameter ? parameter.value : null;
  };

  const isSurgeLogixSynced = (surgeLogix: SurgeLogix) => {
    const valueNames: SurgeLogixValueName[] = ["Count", "Duration", "Adjust"];
    return valueNames.every(valueName => {
      const parameterNumber = getSurgeLogixParameterNumber(surgeLogix, valueName);
      const pumpValue = getPumpParameterValue(parameterNumber);
      return pumpValue === getSurgeLogixValue(surgeLogix, valueName);
    });
  };

  const allSurgeLogixSynced =
    pumpParametersLoaded &&
    surgeLogixs.length > 0 &&
    surgeLogixs.every(isSurgeLogixSynced);

  const renderTableTitle = () => {
    const infoOpen = Boolean(pumpParameterInfoAnchor);

    return (
      <span className={classes.tableTitle}>
        <span>surgeLogix Steps</span>
        {pumpParametersLoaded && (
          <mui.Tooltip title={allSurgeLogixSynced ? "Synced with pump" : "Pump values do not match"}>
            <span className={classes.syncStatus}>
              {allSurgeLogixSynced ? (
                <CheckCircleIcon className={classes.syncedIcon} />
              ) : (
                <ErrorOutlineIcon className={classes.notSyncedIcon} />
              )}
              <span className={allSurgeLogixSynced ? classes.syncedText : classes.notSyncedText}>
                {allSurgeLogixSynced ? "Synced" : "Not synced"}
              </span>
            </span>
          </mui.Tooltip>
        )}
        <mui.Tooltip title="Pump parameter values">
          <mui.IconButton
            aria-label="Pump parameter values"
            className={classes.infoButton}
            size="small"
            onClick={event => setPumpParameterInfoAnchor(event.currentTarget)}
          >
            <InfoOutlinedIcon fontSize="small" />
          </mui.IconButton>
        </mui.Tooltip>
        <mui.Popover
          open={infoOpen}
          anchorEl={pumpParameterInfoAnchor}
          onClose={() => setPumpParameterInfoAnchor(null)}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left"
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left"
          }}
        >
          <div className={classes.parameterInfo}>
            <div className={classes.parameterInfoHeader}>
              <div className={classes.parameterInfoHeading}>Pump parameter values</div>
              <mui.Tooltip title="Refresh pump parameters">
                <mui.IconButton
                  aria-label="Refresh pump parameters"
                  size="small"
                  onClick={fetchPumpParameters}
                >
                  <RefreshIcon fontSize="small" />
                </mui.IconButton>
              </mui.Tooltip>
            </div>
            {pumpParametersLoaded ? (
              surgeLogixs.map(surgeLogix => (
                <div key={`${surgeLogix.id}-${surgeLogix.type}`} className={classes.parameterInfoGroup}>
                  <div className={classes.parameterInfoTitle}>
                    {surgeLogix.fieldName} {surgeLogix.type}
                  </div>
                  {(["Count", "Duration", "Adjust"] as SurgeLogixValueName[]).map(valueName => {
                    const parameterNumber = getSurgeLogixParameterNumber(surgeLogix, valueName);
                    const pumpValue = getPumpParameterValue(parameterNumber);
                    const expectedValue = getSurgeLogixValue(surgeLogix, valueName);
                    const valueMatches = pumpValue === expectedValue;

                    return (
                      <div key={`${surgeLogix.id}-${valueName}`} className={classes.parameterInfoRow}>
                        <span>{valueName}</span>
                        <span>Parm {parameterNumber}</span>
                        <span>Value {pumpValue === null ? "Missing" : pumpValue}</span>
                        <span className={valueMatches ? classes.parameterMatch : classes.parameterMismatch}>
                          {valueMatches ? "OK" : `Expected ${expectedValue}`}
                        </span>
                      </div>
                    );
                  })}
                </div>
              ))
            ) : (
              <div>Loading pump parameters...</div>
            )}
          </div>
        </mui.Popover>
      </span>
    );
  };

  useEffect(() => {
    try {
      fetchData();
      fetchPumpParameters();
      // @ts-ignore
    } catch (e) {
      // @ts-ignore
      console.log(e.message);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (props.pumpParameterRefreshKey > 0) {
      fetchPumpParameters();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.pumpParameterRefreshKey]);

  const headCells: HeaderCells<SurgeLogix>[] = [
    {
      id: "fieldName",
      numeric: false,
      disablePadding: true,
      label: "Field name",
      accessor: h => h.fieldName
    },
    {
      id: "type",
      numeric: false,
      disablePadding: true,
      label: "Type",
      accessor: h => h.type
    },
    {
      id: "count",
      numeric: true,
      disablePadding: true,
      label: "Cycle Count",
      accessor: h => h.count
    },
    {
      id: "duration",
      numeric: true,
      disablePadding: false,
      label: "Set Duration(min)",
      accessor: h => h.duration
    },
    {
      id: "adjust",
      numeric: true,
      disablePadding: false,
      label: "Cycle Time Adjust (%)",
      accessor: h => h.adjust
    }
  ];

  return (
    <>
      <MaterialTable
        title={renderTableTitle()}
        headerCells={headCells}
        rows={surgeLogixs}
        defaultRowsPerPage={12}
        showAddButton={false}
        idPropertyAccessor={r => r.id}
        handleAddOrUpdate={addUpdateSurgeLogix}
        handleDelete={zeroSurgeLogix}
      />
      <EditSurgeLogix
        isVisible={showAddNew}
        close={finishOperation}
        surgeLogix={selectedSurgeLogix}
        updateSurgeLogix={surgeLogixApi.putOne}
        markStepsChanged={props.markStepsChanged}
      />
    </>
  );
};

const useStyles = makeStyles((theme: any) => ({
  root: {
    width: "100%",
    marginBottom: theme.standardMargin
  },
  tableContainer: {
    maxHeight: "40vh"
  },
  headerSection: {
    display: "flex",
    maxWidth: "900px",
    justifySelf: "center",
    marginBottom: theme.standardMargin,
    "& div": {
      marginLeft: ".5em"
    },
    "& button": {
      marginLeft: ".5em"
    }
  },
  serialNumber: {
    color: theme.textColor,
    fontSize: "1em",
    fontWeight: "normal"
  },
  sectionHeader: {
    color: theme.selectedItemColor,
    justifySelf: "start",
    fontSize: "1.25em",
    fontWeight: "bold",
    display: "flex",
    flexDirection: "row",
    width: "100%",
    justifyContent: "space-between"
  },
  cardHeader: {
    display: "flex",
    flexDirection: "row",
    width: "100%",
    justifyContent: "space-between",
    alignItems: "center"
  },
  informationRoot: {
    width: "100%"
  },
  AutoControlButton: {
    backgroundColor: theme.mainBackgroundColor,
    color: theme.selectedItemColor,
    textTransform: "none"
  },
  tableTitle: {
    alignItems: "center",
    display: "inline-flex"
  },
  infoButton: {
    marginLeft: theme.smallMargin
  },
  parameterInfo: {
    maxWidth: "520px",
    padding: theme.standardMargin
  },
  parameterInfoGroup: {
    marginBottom: theme.standardMargin,
    "&:last-child": {
      marginBottom: 0
    }
  },
  parameterInfoTitle: {
    color: theme.selectedItemColor,
    fontWeight: "bold",
    marginBottom: theme.smallMargin
  },
  parameterInfoRow: {
    display: "grid",
    gap: theme.smallMargin,
    gridTemplateColumns: "80px 90px 90px minmax(90px, 1fr)",
    marginBottom: "4px",
    whiteSpace: "nowrap"
  },
  parameterMatch: {
    color: theme.palette.success.main,
    fontWeight: "bold"
  },
  parameterMismatch: {
    color: theme.palette.error.main,
    fontWeight: "bold"
  },
  syncStatus: {
    alignItems: "center",
    display: "inline-flex",
    marginLeft: theme.smallMargin,
    minHeight: "24px",
    justifyContent: "center",
    whiteSpace: "nowrap"
  },
  syncedIcon: {
    color: theme.palette.success.main
  },
  notSyncedIcon: {
    color: theme.palette.error.main
  },
  syncedText: {
    color: theme.palette.success.main,
    fontSize: "0.875rem",
    fontWeight: "bold",
    marginLeft: "4px"
  },
  notSyncedText: {
    color: theme.palette.error.main,
    fontSize: "0.875rem",
    fontWeight: "bold",
    marginLeft: "4px"
  },
  parameterInfoHeader: {
    alignItems: "center",
    display: "flex",
    justifyContent: "space-between",
    marginBottom: theme.smallMargin
  },
  parameterInfoHeading: {
    color: theme.selectedItemColor,
    fontWeight: "bold"
  }
}));

export default SurgeLogixPage;
